import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
  BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import {
  Trophy, Target, GraduationCap, Award, TrendingUp, Zap, Flame,
  Crown, Medal, Building2, Users2, Loader2, ArrowUpRight,
} from "lucide-react";

const rankIcon = (r) =>
  r === 1 ? <Crown size={16} className="text-yellow-400" /> :
  r === 2 ? <Medal size={16} className="text-slate-300" /> :
  r === 3 ? <Medal size={16} className="text-amber-600" /> :
  <span className="text-xs text-brand-muted w-4 text-center">{r}</span>;

function StatCard({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="p-5 rounded-sm bg-brand-surface border border-brand-border hover:border-brand-blue/40 transition-colors">
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${accent}`}>
          <Icon size={18} />
        </div>
      </div>
      <p className="font-heading font-black text-3xl mt-4">{value}</p>
      <p className="text-sm text-brand-muted mt-0.5">{label}</p>
      {sub && <p className="text-xs text-brand-orange mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user.role === "master" || user.role === "coach") return;
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, [user.role]);

  if (user.role === "master") return <Navigate to="/app/rede" replace />;
  if (user.role === "coach") return <Navigate to="/app/coaching" replace />;

  if (!data) {
    return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;
  }

  const s = data.stats;
  const isAdmin = user.role === "admin";

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl">Olá, {user.name.split(" ")[0]} 👋</h1>
          <p className="text-brand-muted text-sm mt-1">
            {isAdmin ? "Visão geral da plataforma Alliance 4 Growth." : "Aqui está o teu progresso na A4G League."}
          </p>
        </div>
        {!isAdmin && (
          <div className="px-4 py-2 rounded-sm bg-gradient-to-r from-brand-blue/20 to-brand-orange/20 border border-brand-border">
            <span className="text-xs text-brand-muted">Nível</span>
            <p className="font-heading font-bold text-lg leading-none mt-0.5">{s.level_name}</p>
          </div>
        )}
      </div>

      {/* Admin platform stats */}
      {isAdmin && data.platform && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <StatCard icon={Building2} label="Agências" value={data.platform.agencies} accent="bg-brand-blue/15 text-brand-blue" />
          <StatCard icon={Zap} label="Clientes ativos" value={data.platform.active_clients} accent="bg-brand-success/15 text-brand-success" />
          <StatCard icon={Users2} label="Consultores" value={data.platform.consultants} accent="bg-brand-orange/15 text-brand-orange" />
          <StatCard icon={GraduationCap} label="Módulos" value={data.platform.modules} accent="bg-brand-blue/15 text-brand-blue" />
          <StatCard icon={Target} label="Objetivos ativos" value={data.platform.goals} accent="bg-brand-orange/15 text-brand-orange" />
        </div>
      )}

      {/* Consultant stat cards */}
      {!isAdmin && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Trophy} label="Pontos totais" value={s.points} accent="bg-brand-orange/15 text-brand-orange" sub={s.points_to_next > 0 ? `+${s.points_to_next} p/ ${s.next_level_name}` : "Nível máximo!"} />
          <StatCard icon={Target} label="Objetivos concluídos" value={s.goals_completed} accent="bg-brand-blue/15 text-brand-blue" />
          <StatCard icon={GraduationCap} label="Módulos concluídos" value={s.modules_completed} accent="bg-brand-success/15 text-brand-success" />
          <StatCard icon={Award} label="Badges" value={`${s.badges_earned}/${s.badges.length}`} accent="bg-brand-orange/15 text-brand-orange" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Level progress */}
          {!isAdmin && (
            <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Flame size={18} className="text-brand-orange" />
                  <h3 className="font-heading font-semibold">Progresso de nível</h3>
                </div>
                <span className="text-sm text-brand-muted">{s.progress_pct}%</span>
              </div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-bold text-white">{s.level_name}</span>
                <span className="text-brand-muted">{s.next_level_name}</span>
              </div>
              <div className="h-3 bg-brand-border rounded-full overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${s.progress_pct}%` }} transition={{ duration: 0.8 }}
                  className="h-full bg-gradient-to-r from-brand-blue to-brand-orange rounded-full" />
              </div>
            </div>
          )}

          {/* Weekly chart */}
          <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} className="text-brand-blue" />
              <h3 className="font-heading font-semibold">Pontos nos últimos 7 dias</h3>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart}>
                  <XAxis dataKey="day" stroke="#8B92A5" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }}
                    contentStyle={{ background: "#0F1115", border: "1px solid #1F232D", borderRadius: 6, color: "#fff" }} />
                  <Bar dataKey="points" radius={[4, 4, 0, 0]}>
                    {data.chart.map((_, i) => <Cell key={i} fill={i === data.chart.length - 1 ? "#FF5C00" : "#1A56FF"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Active goals */}
          <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target size={18} className="text-brand-orange" />
                <h3 className="font-heading font-semibold">Objetivos ativos</h3>
              </div>
              <button onClick={() => navigate("/app/objetivos")} className="text-xs text-brand-blue hover:underline flex items-center gap-1">
                Ver todos <ArrowUpRight size={13} />
              </button>
            </div>
            <div className="space-y-2">
              {data.goals.slice(0, 4).map((g) => (
                <div key={g.id} className="flex items-center justify-between p-3 rounded-sm border border-brand-border hover:border-brand-blue/40 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{g.title}</p>
                    <span className="text-xs text-brand-muted">{g.category} · {g.scope === "team" ? "Equipa" : "Individual"}</span>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-sm shrink-0 ${g.completed ? "bg-brand-success/15 text-brand-success" : "bg-brand-orange/15 text-brand-orange"}`}>
                    {g.completed ? "Concluído" : `+${g.points_reward}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Leaderboard */}
          <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={18} className="text-brand-orange" />
              <h3 className="font-heading font-semibold">Ranking da equipa</h3>
            </div>
            <div className="space-y-1">
              {data.leaderboard.map((e) => (
                <div key={e.id} data-testid="dashboard-leaderboard-row"
                  className={`flex items-center gap-3 p-2.5 rounded-sm ${e.is_me ? "bg-brand-blue/15 border border-brand-blue/30" : ""}`}>
                  <div className="w-5 flex justify-center">{rankIcon(e.rank)}</div>
                  <div className="h-8 w-8 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center text-xs font-bold text-white shrink-0">
                    {e.name[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{e.name}{e.is_me && " (tu)"}</p>
                    <p className="text-xs text-brand-muted">{e.level_name}</p>
                  </div>
                  <span className="text-sm font-bold text-brand-orange">{e.points}</span>
                </div>
              ))}
              {data.leaderboard.length === 0 && <p className="text-sm text-brand-muted">Sem dados de ranking.</p>}
            </div>
          </div>

          {/* Badges */}
          {!isAdmin && (
            <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
              <div className="flex items-center gap-2 mb-4">
                <Award size={18} className="text-brand-blue" />
                <h3 className="font-heading font-semibold">Conquistas</h3>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {s.badges.map((b) => (
                  <div key={b.id} title={`${b.name} — ${b.desc}`}
                    className={`aspect-square rounded-sm flex items-center justify-center text-center p-1 ${
                      b.earned ? "bg-gradient-to-br from-brand-blue to-brand-orange badge-shine glow-orange" : "bg-brand-border/40 opacity-40"
                    }`}>
                    <Award size={20} className="text-white" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
